import React, { useMemo } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import {
  EntityType,
  EntityTypesSupportedByMapsPage,
  MapEntity,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";

import ButtonLink from "@app/component/ButtonLink";
import { getSelectedEntities } from "@app/scene/map/store/mapMode/selectors";
import RecordPreviewConfigurationColumns from "@app/store/recordPreview/RecordPreviewConfigurationColumns";
import { getRecordPreviewConfigurationColumns } from "@app/store/recordPreview/selectors";
import { RootState } from "@app/store/rootReducer";

import styles from "./GroupsRoutesPinInfo.module.scss";

const messages = defineMessages({
  info: {
    id: "map.recordsList.groupsRoutesPinInfo",
    defaultMessage:
      "{groupsAlreadyPinned, select, false {{routesAlreadyPinned, select, false {Groups and routes} other {Groups}}} other {Routes}} are pinned for all record types to help you make bulk action decisions while in lasso mode.",
    description: "Records list Groups and Routes auto-pin info - only routes",
  },
  undo: {
    id: "map.recordsList.groupsRoutesPinInfo.undo",
    defaultMessage: "Undo",
    description: "Records list Groups and Routes auto-pin info - undo action",
  },
});

interface Props {
  columns: RecordPreviewConfigurationColumns;
  entities: MapEntity[];
  onUndo: () => void;
}

const GroupsRoutesPinInfo: React.FC<Props> = ({ columns, entities, onUndo }) => {
  const intl = useIntl();

  const [groupsAlreadyPinned, routesAlreadyPinned] = useMemo(() => {
    const selectedEntityTypes: Set<EntityTypesSupportedByMapsPage> = new Set(
      entities.map(({ entity }) => entity)
    );
    return [
      ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as EntityTypesSupportedByMapsPage[])
        .filter((type) => selectedEntityTypes.has(type))
        .every((type) =>
          columns[type].some((field: IField) => field.hasFeature(FieldFeature.GROUP_FIELD))
        ),
      ([EntityType.COMPANY, EntityType.PERSON] as EntityTypesSupportedByMapsPage[])
        .filter((type) => selectedEntityTypes.has(type))
        .every((type) =>
          columns[type].some((field: IField) => field.hasFeature(FieldFeature.ROUTE_FIELD))
        ),
    ];
  }, [columns, entities]);

  if (groupsAlreadyPinned && routesAlreadyPinned) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div>{intl.formatMessage(messages.info, { groupsAlreadyPinned, routesAlreadyPinned })}</div>
      <ButtonLink className={styles.undo} onClick={onUndo}>
        {intl.formatMessage(messages.undo)}
      </ButtonLink>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  columns: getRecordPreviewConfigurationColumns(state),
  entities: getSelectedEntities(state),
});

export default connect(mapStateToProps)(GroupsRoutesPinInfo);
